<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import AgentActivityAlert from './AgentActivityAlert.vue';
import QualifiedVisitorAlert from './QualifiedVisitorAlert.vue';
import VisitNowAnchor from './VisitNowAnchor.vue';
import VisitSectionTitle from './VisitSectionTitle.vue';
import VisitsListErrorState from './VisitsListErrorState.vue';
import VisitsListSkeleton from './VisitsListSkeleton.vue';

const props = defineProps({
  visits: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  loadError: { type: Boolean, default: false },
  isCurrentDate: { type: Boolean, default: false },
  lastUpdatedLabel: { type: String, default: '' },
  dayLabel: { type: String, default: '' },
  showAgentActivityAlert: { type: Boolean, default: false },
  hasQualifiedVisitor: { type: Boolean, default: false },
  errorTitle: { type: String, default: 'Não foi possível carregar a agenda' },
  errorDescription: {
    type: String,
    default: 'Tente atualizar a página para carregar suas visitas.',
  },
  emptyDescription: {
    type: String,
    default: 'Você ainda não possui visitas agendadas para esse dia.',
  },
});

const emit = defineEmits(['retry', 'agent-activity']);

const visitDateValue = visita => visita?.scheduledAt || visita?.scheduled_at;

const visitTime = visita => {
  const date = new Date(visitDateValue(visita) || 0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formatTime = value =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const visitTimeLabel = visita => {
  const value = visitDateValue(visita);
  return value ? formatTime(value) : 'Sem horário';
};

const groupedVisits = computed(() => {
  const groups = [];
  const byTime = new Map();

  [...props.visits]
    .sort((a, b) => visitTime(a) - visitTime(b))
    .forEach(visita => {
      const label = visitTimeLabel(visita);
      if (!byTime.has(label)) {
        const dateValue = visitDateValue(visita);
        const time = dateValue ? new Date(dateValue).getTime() : 0;
        const group = {
          id: `${label}-${time}`,
          label,
          time,
          visits: [],
        };
        byTime.set(label, group);
        groups.push(group);
      }
      byTime.get(label).visits.push(visita);
    });

  return groups;
});

const timelineRows = computed(() => {
  if (!props.isCurrentDate) {
    return groupedVisits.value.map(group => ({
      type: 'visits',
      ...group,
    }));
  }

  const now = Date.now();
  const rows = [];
  let insertedNow = false;

  groupedVisits.value.forEach(group => {
    if (!insertedNow && group.time >= now) {
      rows.push({ type: 'now', id: 'now' });
      insertedNow = true;
    }
    rows.push({ type: 'visits', ...group });
  });

  if (!insertedNow) rows.push({ type: 'now', id: 'now' });

  return rows;
});

const updatedAtText = computed(() =>
  props.lastUpdatedLabel
    ? `Última atualização em ${props.lastUpdatedLabel}`
    : ''
);
</script>

<template>
  <section class="flex-1 overflow-auto bg-white">
    <div class="mx-auto grid w-full max-w-[536px] gap-3 px-4 py-4 sm:px-6">
      <AgentActivityAlert
        :show="showAgentActivityAlert"
        @action="emit('agent-activity')"
      />
      <QualifiedVisitorAlert :show="hasQualifiedVisitor" />

      <p
        v-if="updatedAtText"
        class="text-center text-[12px] italic text-lais-text-muted"
      >
        {{ updatedAtText }}
      </p>

      <VisitsListSkeleton v-if="isLoading && visits.length === 0" />
      <VisitsListErrorState
        v-else-if="loadError"
        :title="errorTitle"
        :description="errorDescription"
        @retry="emit('retry')"
      />
      <div
        v-else-if="!visits.length"
        class="grid min-h-[260px] place-items-center py-8 text-center text-lais-text-muted"
      >
        <div>
          <span
            class="i-lucide-calendar-days mx-auto mb-3 block size-10 text-lais-text-empty"
            aria-hidden="true"
          />
          <p class="text-[14px] font-semibold text-lais-text-body">
            {{ emptyDescription }}
          </p>
        </div>
      </div>
      <template v-else>
        <VisitSectionTitle :label="dayLabel" />
        <template v-for="row in timelineRows" :key="row.id">
          <VisitNowAnchor v-if="row.type === 'now'" />
          <div v-else class="grid gap-3 sm:grid-cols-[56px_minmax(0,1fr)]">
            <time
              class="pt-3 text-[13px] font-semibold text-lais-text-muted"
              :datetime="row.label"
            >
              {{ row.label }}
            </time>
            <div class="grid min-w-0 gap-3">
              <slot
                v-for="visita in row.visits"
                :key="visita.id || visita.visitCode || visita.visit_code"
                name="visit"
                :visita="visita"
              />
            </div>
          </div>
        </template>
      </template>
    </div>
  </section>
</template>
